/* The co-owned chain as a real DAG (Ale rulings 15a–15c, 2026-09-03).
 *
 * A co-owned asset (held by 2+ holders) sits ONCE on its level with one
 * amber wire per holder into it, each wire carrying the holder's figure;
 * wires that would cross are gentle curves of the boxes' corner feel
 * (15a). Boxes are absolutely positioned HTML — the same rings, tints and
 * colours as the fan-bar tree — over an SVG wire underlay whose geometry
 * `coownedLayout` computes. Focus lights an object's vertical section UP
 * through ALL its holders, co-owners included (the section already gathers
 * every leg into a co-owned issuer — see focus.ts). No density cutoff: the
 * wires scale to any N, and where a band's wires cross, that band is
 * lengthened to give the curves room to separate (coownedLayout). */

import { useMemo, useState } from 'react'
import type { DrawerTarget } from '../ObjectDrawer'
import type { RecordTablePayload, RtSegment } from '../RecordTable'
import type { WalkNode } from './fundArea'
import { dagLayout } from './coownedLayout'
import { instrumentTradesOf } from './chainTree'
import { fmt, positionFigures } from './overviewFigures'
import WalkChip from './WalkChip'

type Lit = 'on' | 'dim' | undefined

interface Props {
  /** The fund page this DAG renders inside — the walk door carries it. */
  fundId?: string
  table: RecordTablePayload
  chain: RtSegment[]
  walk: WalkNode[]
  litIds: Set<string> | null
  section: RtSegment[]
  hasFocus: boolean
  onOpen: (target: DrawerTarget) => void
  onJump: (target: DrawerTarget) => void
}

const focusCls = (state: Lit) => (state === 'on' ? ' focus' : state === 'dim' ? ' dim' : '')

export default function CoOwnedChain({
  fundId,
  table,
  chain,
  walk,
  litIds,
  section,
  hasFocus,
  onOpen,
  onJump,
}: Props) {
  const layout = useMemo(() => dagLayout(chain), [chain])
  /* Hybrid label reveal (Ale 2026-09-03): a wire that CROSSES nothing keeps
   * its label shown always (it can't overlap anyone) — the clean static
   * way. A wire that is part of a crossing set hides its label until hover,
   * because that is the crowded band. On hovering a crossing wire we reveal
   * ONLY its label and highlight its NEIGHBOURHOOD — every other line from
   * the same holder AND every other line into the same instrument (its
   * co-owners) stays at normal opacity; only the truly unrelated crossing
   * lines (different holder AND different instrument) fade. The non-crossing
   * wires, their labels and the nodes stay intact. `hover` is the key of the
   * hovered wire; `nodeHover` the id of a highlighted node. */
  const [hover, setHover] = useState<string | null>(null)
  const [nodeHover, setNodeHover] = useState<string | null>(null)
  const wireKey = (w: (typeof layout.wires)[number], i: number) =>
    `${w.kind}-${w.positionId ?? w.instrumentId}-${i}`
  /* key → the wire, so we can read the hovered wire's holder/instrument and
   * decide which other lines are its neighbourhood. */
  const wireByKey = useMemo(
    () =>
      new Map(layout.wires.map((w, i) => [`${w.kind}-${w.positionId ?? w.instrumentId}-${i}`, w])),
    [layout.wires]
  )
  const hoveredWire = hover ? (wireByKey.get(hover) ?? null) : null
  const hoverCrosses = !!hoveredWire?.crosses
  /* A wire is in the hovered wire's neighbourhood when it shares the holder
   * (same holder's other legs) or the target instrument (the co-owners of
   * that instrument). Only unrelated crossing lines dim. */
  const related = (w: (typeof layout.wires)[number]): boolean => {
    if (!hoveredWire) return false
    const sameHolder = !!hoveredWire.holderId && !!w.holderId && w.holderId === hoveredWire.holderId
    const sameInstrument =
      !!hoveredWire.instrumentId && !!w.instrumentId && w.instrumentId === hoveredWire.instrumentId
    return sameHolder || sameInstrument
  }
  const entityById = useMemo(() => new Map(table.entities.map((e) => [e.id, e])), [table.entities])
  const instrumentById = useMemo(
    () => new Map(table.instruments.map((i) => [i.id, i])),
    [table.instruments]
  )
  const segByPos = useMemo(() => new Map(chain.map((s) => [s.position_id, s])), [chain])
  const holderIds = useMemo(() => new Set(chain.map((s) => s.holder_id)), [chain])

  const lit = (id: string | null): Lit =>
    !litIds ? undefined : id && litIds.has(id) ? 'on' : 'dim'
  const legLit = (positionId: string): Lit =>
    !hasFocus ? undefined : section.some((x) => x.position_id === positionId) ? 'on' : 'dim'

  const tradesOf = (s: RtSegment) =>
    table.trades.filter(
      (t) =>
        t.position_id && (s.position_ids.includes(t.position_id) || t.position_id === s.position_id)
    ).length
  const tradeWords = (kinds: string[]) => {
    const n = kinds.length
    const one = kinds.every((k) => k === kinds[0]) ? kinds[0].replace(/_/g, ' ') : 'trade'
    return `${n} ${one}${n === 1 ? '' : 's'}`
  }

  if (layout.overflow)
    return (
      <div
        style={{ fontSize: 'var(--text-12)', color: 'var(--fa-faint)', padding: 20 }}
        data-testid="coowned-overflow"
      >
        This chain is too large to wire on one canvas.
      </div>
    )

  return (
    <div
      className="coowned-chain"
      data-testid="coowned-chain"
      style={{ position: 'relative', width: layout.width, height: layout.height }}
    >
      {/* the wire underlay — one gentle curve per leg, in the object's
          colour; a crossing wire bows, it never lies over another (15a).
          A fat transparent hit-path over each wire makes the thin curve
          easy to hover; hovering it reveals that one wire's label. */}
      <svg
        className="coowned-wires"
        width={layout.width}
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        aria-hidden="true"
      >
        {layout.wires.map((w, i) => {
          const key = wireKey(w, i)
          const state =
            w.kind === 'pos' && w.positionId ? legLit(w.positionId) : lit(w.instrumentId)
          const touchesNode =
            !!nodeHover &&
            (w.holderId === nodeHover || w.instrumentId === nodeHover || w.issuerId === nodeHover)
          const isHovered = hover === key
          const hot = isHovered || touchesNode
          // while a crossing wire is hovered, dim ONLY the truly unrelated
          // crossing lines (different holder AND different instrument); the
          // hovered wire, its holder's other legs and its instrument's
          // co-owners stay at normal opacity. Non-crossing wires are left be.
          const faded = hoverCrosses && w.crosses && !isHovered && !related(w)
          return (
            <g key={key}>
              <path
                d={w.path}
                className={`cw ${w.kind}${focusCls(state)}${hot ? ' hot' : ''}${faded ? ' faded' : ''}`}
                data-testid="coowned-wire"
                data-kind={w.kind === 'pos' ? 'position' : 'instrument'}
                data-position={w.positionId}
                data-holder={w.holderId ?? undefined}
                data-into={w.instrumentId ?? undefined}
                data-issuer={w.issuerId ?? undefined}
                data-focus={state}
                data-hot={hot ? 'on' : undefined}
                data-faded={faded ? 'on' : undefined}
                fill="none"
              />
              <path
                d={w.path}
                className="cw-hit"
                data-testid="coowned-wire-hit"
                data-kind={w.kind === 'pos' ? 'position' : 'instrument'}
                data-position={w.positionId}
                data-holder={w.holderId ?? undefined}
                data-into={w.instrumentId ?? undefined}
                fill="none"
                onMouseEnter={() => setHover(key)}
                onMouseLeave={() => setHover((h) => (h === key ? null : h))}
              />
            </g>
          )
        })}
      </svg>

      {/* the figures — a NON-crossing wire's label rides its curve and is
          shown always (it can't overlap anyone); a crossing wire's label is
          hidden until that wire is hovered, so the crowded band reads on
          demand and at most one crossing label is ever up (Ale 2026-09-03) */}
      {layout.wires.map((w, i) => {
        if (w.kind !== 'pos' || !w.positionId || w.figX === undefined || w.figY === undefined)
          return null
        const key = wireKey(w, i)
        const shown = !w.crosses || hover === key
        if (!shown) return null
        const s = segByPos.get(w.positionId)
        if (!s) return null
        const state = legLit(w.positionId)
        const trades = tradesOf(s)
        return (
          <div
            key={`fig-${w.positionId}-${i}`}
            className={`cw-fig ${w.crosses ? 'onhover' : 'static'}${focusCls(state)}`}
            data-testid="graph-position"
            data-crossing={w.crosses ? 'yes' : 'no'}
            style={{ left: w.figX, top: w.figY }}
            onMouseEnter={() => setHover(key)}
            onMouseLeave={() => setHover((h) => (h === key ? null : h))}
          >
            <span className="fa-shade pos">{positionFigures(s)}</span>
            {trades > 0 ? (
              <button
                type="button"
                className="fa-tchip pos"
                data-testid="ov-trade-chip"
                onClick={() => onJump({ type: 'position', id: s.position_id })}
              >
                {trades} trade{trades === 1 ? '' : 's'} ›
              </button>
            ) : null}
          </div>
        )
      })}

      {/* instrument trades (an issuance, a buy-back — no position): a blue
          chip on the instrument → issuer wire — shown always for a
          non-crossing paper, on hover for one in a crossing set */}
      {layout.wires.map((w, i) => {
        if (w.kind !== 'ins' || !w.instrumentId) return null
        const key = wireKey(w, i)
        const shown = !w.crosses || hover === key
        if (!shown) return null
        const trades = instrumentTradesOf(table.trades, w.instrumentId)
        if (trades.length === 0) return null
        const state = lit(w.instrumentId)
        return (
          <div
            key={`itr-${w.instrumentId}-${i}`}
            className={`cw-itrade ${w.crosses ? 'onhover' : 'static'}${focusCls(state)}`}
            style={{
              left: w.figX ?? (w.fromX + w.toX) / 2,
              top: w.figY ?? (w.fromY + w.toY) / 2,
            }}
            onMouseEnter={() => setHover(key)}
            onMouseLeave={() => setHover((h) => (h === key ? null : h))}
          >
            <button
              type="button"
              className="fa-tchip ins"
              data-testid="ov-instrument-trade-chip"
              onClick={() => onJump({ type: 'instrument', id: w.instrumentId as string })}
            >
              {tradeWords(trades.map((t) => t.transaction_type))} ›
            </button>
          </div>
        )
      })}

      {/* the boxes — entities (thick navy ring) and instruments (thin blue
          ring, blue text), each drawn ONCE, a door into the Record */}
      {layout.boxes.map((b) => {
        const state = lit(b.id)
        const isEntity = b.kind === 'entity'
        const e = isEntity ? entityById.get(b.id) : null
        const ins = !isEntity ? instrumentById.get(b.id) : null
        const issued = ins?.outstanding_units ?? null
        const bottom = isEntity && !holderIds.has(b.id) // a chain bottom: holds nothing below
        const walkNode = isEntity ? walk.find((n) => n.entity_id === b.id) : undefined
        return (
          <div
            key={`${b.kind}-${b.id}`}
            className="coowned-cell"
            style={{ left: b.cx - b.w / 2, top: b.top, width: b.w }}
          >
            <button
              type="button"
              className={`fa-nbox ${isEntity ? 'e' : 'i'}${focusCls(state)}`}
              data-testid="graph-box"
              data-kind={b.kind}
              data-entity={isEntity ? b.id : undefined}
              data-instrument={!isEntity ? b.id : undefined}
              data-depth={b.depth}
              data-coowned={bottom ? 'asset' : undefined}
              data-focus={state}
              aria-pressed={state === 'on' ? true : undefined}
              onMouseEnter={() => setNodeHover(b.id)}
              onMouseLeave={() => setNodeHover((n) => (n === b.id ? null : n))}
              onClick={() =>
                onOpen(isEntity ? { type: 'entity', id: b.id } : { type: 'instrument', id: b.id })
              }
            >
              <div className="nm">{b.name ?? (isEntity ? 'entity' : 'instrument')}</div>
              {isEntity && e ? (
                <div className="meta">
                  {[e.legal_form, e.jurisdiction].filter(Boolean).join(', ') ||
                    e.entity_nature?.replace(/_/g, ' ')}
                </div>
              ) : null}
              {!isEntity && ins ? (
                issued !== null ? (
                  <div className="meta ibal" data-testid="instrument-issued">
                    {fmt(issued)} issued
                  </div>
                ) : (
                  <div className="meta ns" data-testid="instrument-issued">
                    issued amount not stated
                  </div>
                )
              ) : null}
            </button>
            {bottom && walkNode ? <WalkChip node={walkNode} fundId={fundId} /> : null}
          </div>
        )
      })}
    </div>
  )
}
